import Versions from '@jupiterone/react-brand-icons/jupiterone/versions';
import {
  AppBar,
  Dialog,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Slide,
  SlideProps,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { useState } from 'react';
import { Folder } from 'react-feather';
import { Entity } from '@jupiterone/jupiter-web-toolkit/types/';
import { JupiterTheme, CloseButton } from '@jupiterone/jupiter-web-toolkit';
import RawDataComparisonVersionsLoader from './RawDataComparisonVersionsLoader';
import RawDataLoader from './RawDataLoader';
import RawDataObjectsLoader from './RawDataObjectsLoader';
import RawDataVersionsLoader from './RawDataVersionsLoader';
import { RawDataVersion } from './types';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    appBar: {
      background: theme.palette.coolGrey[700],
      display: 'flex',
    },
    closeButton: {
      color: theme.palette.common.white,
      opacity: 0.75,
      marginRight: theme.spacing(-2),
      '&:hover': {
        opacity: 1,
      },
    },
    title: {
      color: 'white',
      flex: 1,
    },
    dialogPaper: {
      background: theme.palette.coolGrey[50],
      boxShadow: 'none',
    },
    rawData: {
      position: 'relative',
      display: 'flex',
      width: '100%',
      height: '100%',
    },
    drawerPaper: {
      position: 'relative',
      minWidth: theme.spacing(25),
    },
    icon: {
      paddingRight: theme.spacing(1),
    },
  })
);

const Transition = React.forwardRef<unknown, SlideProps>((props, ref) => (
  <Slide direction="up" {...props} ref={ref} />
));

Transition.displayName = 'Transition';

interface Props {
  open: boolean;
  handleClose: VoidFunction;
  entity: Entity;
}

const RawDataModal = ({ open, handleClose, entity }: Props) => {
  const classes = useStyles();

  const [versions, setVersions] = useState<RawDataVersion[]>([]);

  const [selectedRawDataObject, setSelectedRawDataObject] = useState<
    string | undefined
  >();
  const [selectedRawDataVersion, setSelectedRawDataVersion] = useState<
    string | undefined
  >();
  const [
    selectedRawDataComparisonVersion,
    setSelectedRawDataComparisonVersion,
  ] = useState<string | undefined>();

  return (
    <Dialog
      open={open}
      fullScreen={true}
      TransitionComponent={Transition}
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      <AppBar className={classes.appBar} position="static">
        <Toolbar>
          <Typography variant="h6" className={classes.title}>
            History
          </Typography>

          <RawDataComparisonVersionsLoader
            rawDataVersions={versions}
            onChange={setSelectedRawDataComparisonVersion}
          />
          <CloseButton
            onClose={handleClose}
            className={classes.closeButton}
            aria-label="Close"
          />
        </Toolbar>
      </AppBar>

      <div className={classes.rawData}>
        <Drawer
          variant="permanent"
          anchor="left"
          classes={{
            paper: classes.drawerPaper,
          }}
        >
          <List>
            <ListItem key="objects">
              <Folder className={classes.icon} />
              <ListItemText primary="Objects" />
            </ListItem>

            <Divider />

            <RawDataObjectsLoader
              entity={entity}
              onChange={(selectedObject) => {
                setSelectedRawDataVersion(undefined);
                setSelectedRawDataComparisonVersion(undefined);
                setSelectedRawDataObject(selectedObject);
              }}
            />

            <Divider />

            <ListItem key="versions">
              <Versions className={classes.icon} />
              <ListItemText primary="Versions" />
            </ListItem>

            <Divider />

            <RawDataVersionsLoader
              entity={entity}
              selectedRawDataObject={selectedRawDataObject}
              onVersionsChange={setVersions}
              onSelectedVersionChange={setSelectedRawDataVersion}
            />
          </List>
        </Drawer>

        <RawDataLoader
          entity={entity}
          selectedRawDataObject={selectedRawDataObject}
          selectedRawDataVersion={selectedRawDataVersion}
          comparisonRawDataVersion={selectedRawDataComparisonVersion}
        />
      </div>
    </Dialog>
  );
};

export default RawDataModal;
